import React, { useState, useRef, useEffect } from 'react';
import { Canvas, useFrame, extend, useThree } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera, Text, TransformControls } from '@react-three/drei';
import * as THREE from 'three';

// Extendemos Three.js con funcionalidades adicionales
extend({ TransformControls });

// Materiales disponibles
const materialTypes = [
  {
    id: 1,
    name: "Madera contrachapada (1.2x2.4m)",
    width: 1.2,
    height: 2.4,
    thickness: 0.018,
    weight: 15,
    color: "#d2b48c",
    pricePerM2: 25
  },
  {
    id: 2,
    name: "MDF (1.2x2.4m)",
    width: 1.2,
    height: 2.4,
    thickness: 0.018,
    weight: 25,
    color: "#a67c52",
    pricePerM2: 40
  },
  {
    id: 3,
    name: "Melamina (1.2x2.4m)",
    width: 1.2,
    height: 2.4,
    thickness: 0.016,
    weight: 20,
    color: "#ffffff",
    pricePerM2: 35
  }
];

const furnitureTypes = [
  {
    id: 1,
    name: "Estantería simple",
    defaultDimensions: { width: 0.8, height: 1.8, depth: 0.3 },
    shelves: 3
  },
  {
    id: 2,
    name: "Armario",
    defaultDimensions: { width: 1.2, height: 2.1, depth: 0.6 },
    shelves: 2,
    doors: 2
  },
  {
    id: 3,
    name: "Mesa",
    defaultDimensions: { width: 1.5, height: 0.75, depth: 0.9 },
    legs: 4
  }
];

const screwWeight = 0.005; // kg por tornillo
const screwPrice = 0.1; // precio por tornillo

// Componente del Grid Vertical
const VerticalGrid = ({ position = [0, 1.2, 0] }) => {
  return (
    <group position={position}>
      <gridHelper 
        args={[5, 20, "#cccccc", "#eeeeee"]} 
        rotation={[Math.PI/2, 0, 0]}
      />
    </group>
  );
};

// Componente de Panel Transformable
const TransformablePanel = ({ 
  panel, 
  isSelected, 
  onClick, 
  onTransform,
  material
}) => {
  const transformRef = useRef();
  
  // Evitar que los eventos del TransformControls afecten a los OrbitControls
  useEffect(() => {
    if (transformRef.current) {
      const callback = (event) => event.value && event.stopPropagation();
      transformRef.current.addEventListener('dragging-changed', callback);
      return () => {
        transformRef.current?.removeEventListener('dragging-changed', callback);
      };
    }
  }, []);

  return (
    <group>
      <TransformControls
        ref={transformRef}
        position={panel.position}
        rotation={panel.rotation}
        enabled={isSelected}
        mode="translate"
        onMouseUp={(e) => {
          if (e.stopped) return;
          const newPosition = transformRef.current?.position.clone();
          if (newPosition) {
            onTransform(panel.id, newPosition.toArray());
          }
        }}
      >
        <mesh
          onClick={(e) => {
            e.stopPropagation();
            onClick(panel.id);
          }}
          onPointerMissed={() => onClick(null)}
          castShadow
        >
          <boxGeometry args={[panel.width, panel.thickness, panel.height]} />
          <meshStandardMaterial 
            color={material.color} 
            roughness={0.6}
            transparent={!isSelected}
            opacity={isSelected ? 1 : 0.8}
          />
        </mesh>
      </TransformControls>
      
      {/* Etiqueta con medidas */}
      {isSelected && (
        <Text
          position={[
            panel.position[0],
            panel.position[1] + panel.thickness/2 + 0.05,
            panel.position[2]
          ]}
          fontSize={0.1}
          color="black"
          anchorX="center"
          anchorY="middle"
          rotation={[Math.PI/2, 0, 0]}
        >
          {`${panel.width.toFixed(2)}m × ${panel.height.toFixed(2)}m`}
        </Text>
      )}
    </group>
  );
};

// Componente de la Escena 3D
const FurnitureScene = ({ 
  panels, 
  selectedPanel, 
  onSelect, 
  onTransform,
  materials,
  furnitureType,
  dimensions
}) => {
  const { camera } = useThree();
  
  // Posicionamos la cámara para una buena vista inicial
  useEffect(() => {
    camera.position.set(2, 2, 3);
    camera.lookAt(0, 1, 0);
  }, [camera]);

  return (
    <>
      <ambientLight intensity={0.5} />
      <spotLight position={[10, 10, 10]} angle={0.15} penumbra={1} intensity={1} castShadow />
      <pointLight position={[-10, -10, -10]} intensity={0.5} />
      
      {/* Grid vertical */}
      <VerticalGrid />
      
      {/* Paneles */}
      {panels.map((panel) => {
        const material = materials.find(m => m.id === panel.materialId) || materials[0];
        return (
          <TransformablePanel
            key={panel.id}
            panel={panel}
            isSelected={selectedPanel === panel.id}
            onClick={onSelect}
            onTransform={onTransform}
            material={material}
          />
        );
      })}
      
      {/* Controles de cámara */}
      <OrbitControls 
        makeDefault 
        enablePan={true} 
        enableZoom={true} 
        enableRotate={true}
      />
      
      {/* Plano de apoyo */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow>
        <planeGeometry args={[5, 5]} />
        <meshStandardMaterial color="#f5f5f5" />
      </mesh>
    </>
  );
};

// Componente principal
const FurnitureDesigner = () => {
  const [furnitureType, setFurnitureType] = useState(furnitureTypes[0]);
  const [dimensions, setDimensions] = useState(furnitureTypes[0].defaultDimensions);
  const [selectedMaterial, setSelectedMaterial] = useState(materialTypes[0]);
  const [shelvesCount, setShelvesCount] = useState(furnitureTypes[0].shelves || 0);
  const [showEdges, setShowEdges] = useState(true);
  const [panels, setPanels] = useState([]);
  const [selectedPanel, setSelectedPanel] = useState(null);
  
  const [results, setResults] = useState({
    panelPieces: [],
    totalWeight: 0,
    materialUsed: 0,
    screws: 0,
    totalCost: 0,
    panelCost: 0,
    screwsCost: 0
  });

  // Calcular materiales y paneles necesarios
  const calculateMaterials = () => {
    const { width, height, depth } = dimensions;
    const { 
      width: panelW = 1.2, 
      height: panelH = 2.4, 
      thickness: panelT = 0.018, 
      weight: panelWeight = 15,
      pricePerM2 = 25
    } = selectedMaterial || {};

    // Calcular paneles necesarios según tipo de mueble
    let requiredPanels = [];
    let totalArea = 0;

    switch(furnitureType.id) {
      case 1: // Estantería
        // Laterales (2)
        requiredPanels.push({ width: depth, height: height, count: 2 });
        // Superior e inferior
        requiredPanels.push({ width: width, height: depth, count: 2 });
        // Estantes
        if (shelvesCount > 0) {
          requiredPanels.push({ width: width, height: depth, count: shelvesCount });
        }
        // Trasera
        requiredPanels.push({ width: width, height: height, count: 1 });
        break;
        
      case 2: // Armario
        // Laterales (2)
        requiredPanels.push({ width: depth, height: height, count: 2 });
        // Superior e inferior
        requiredPanels.push({ width: width, height: depth, count: 2 });
        // Estantes
        if (shelvesCount > 0) {
          requiredPanels.push({ width: width - 0.04, height: depth - 0.02, count: shelvesCount });
        }
        // Puertas (2)
        requiredPanels.push({ width: width/2 - 0.01, height: height - 0.02, count: 2 });
        // Trasera
        requiredPanels.push({ width: width, height: height, count: 1 });
        break;
        
      case 3: // Mesa
        // Tablero superior
        requiredPanels.push({ width: width, height: depth, count: 1 });
        // Patas (4)
        requiredPanels.push({ width: 0.05, height: height - 0.05, count: 4 });
        // Travesaños (opcionales)
        requiredPanels.push({ width: width - 0.1, height: 0.05, count: 4 });
        break;
    }

    // Calcular material necesario
    let materialUsed = 0;
    let panelPieces = [];
    
    requiredPanels.forEach(panel => {
      for (let i = 0; i < panel.count; i++) {
        const area = panel.width * panel.height;
        totalArea += area;
        
        // Ver si cabe en un panel estándar
        if (panel.width <= panelW && panel.height <= panelH) {
          materialUsed += area;
          panelPieces.push({
            width: panel.width,
            height: panel.height,
            type: panel.width === panelW && panel.height === panelH ? 'full' : 'cut',
            area: area
          });
        } else {
          // Si no cabe completo, calcular cortes
          const widthPanels = Math.ceil(panel.width / panelW);
          const heightPanels = Math.ceil(panel.height / panelH);
          
          for (let w = 0; w < widthPanels; w++) {
            for (let h = 0; h < heightPanels; h++) {
              const pieceWidth = Math.min(panelW, panel.width - w * panelW);
              const pieceHeight = Math.min(panelH, panel.height - h * panelH);
              const pieceArea = pieceWidth * pieceHeight;
              
              materialUsed += pieceArea;
              panelPieces.push({
                width: pieceWidth,
                height: pieceHeight,
                type: 'cut',
                area: pieceArea
              });
            }
          }
        }
      }
    });

    // Calcular costos y pesos
    const panelArea = panelW * panelH;
    const panelsWeight = (materialUsed / panelArea) * panelWeight;
    const panelCost = (materialUsed / panelArea) * pricePerM2;
    const screws = Math.ceil(totalArea * 0.5); // Estimación de tornillos
    const screwsWeight = screws * screwWeight;
    const screwsCost = screws * screwPrice;
    const totalWeight = panelsWeight + screwsWeight;
    const totalCost = panelCost + screwsCost;

    setResults({
      panelPieces: panelPieces,
      totalWeight: totalWeight,
      materialUsed: materialUsed,
      screws: screws,
      totalCost: totalCost,
      panelCost: panelCost,
      screwsCost: screwsCost,
      panelsWeight: panelsWeight,
      screwsWeight: screwsWeight
    });

    // Generar los paneles visuales
    generateVisualPanels(panelPieces);
  };

  // Generar paneles visuales para la escena 3D
  const generateVisualPanels = (panelPieces) => {
    const visualPanels = [];
    let idCounter = 1;

    panelPieces.forEach((piece, index) => {
      visualPanels.push({
        id: idCounter++,
        width: piece.width,
        height: piece.height,
        thickness: selectedMaterial.thickness,
        position: [0, selectedMaterial.thickness/2, 0],
        rotation: [0, 0, 0],
        materialId: selectedMaterial.id
      });
    });

    setPanels(visualPanels);
    setSelectedPanel(null);
  };

  const handleDimensionChange = (e) => {
    const { name, value } = e.target;
    setDimensions(prev => ({
      ...prev,
      [name]: Math.max(0.1, parseFloat(value) || 0)
    }));
  };

  const handleFurnitureTypeChange = (e) => {
    const type = furnitureTypes.find(f => f.id === parseInt(e.target.value)) || furnitureTypes[0];
    setFurnitureType(type);
    setDimensions(type.defaultDimensions);
    if (type.shelves) setShelvesCount(type.shelves);
  };

  const handleTransformPanel = (id, newPosition) => {
    setPanels(panels.map(panel => 
      panel.id === id ? { ...panel, position: newPosition } : panel
    ));
  };

  const formatDimension = (value) => {
    const rounded = Math.round(value * 100) / 100;
    return rounded.toFixed(2).replace(/\.?0+$/, '');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(value);
  };

  const renderMaterialList = () => {
    const counts = {};
    results.panelPieces.forEach(piece => {
      const key = `${formatDimension(piece.width)}x${formatDimension(piece.height)}-${piece.type}`;
      counts[key] = (counts[key] || 0) + 1;
    });

    return Object.entries(counts).map(([size, count]) => {
      const [dimensions, type] = size.split('-');
      const [width, height] = dimensions.split('x');
      
      return (
        <div key={size} className="flex justify-between py-1">
          <span className="text-sm">
            Panel {width}m × {height}m ({type === 'full' ? 'entero' : 'cortado'})
          </span>
          <span className="text-sm font-semibold">{count}</span>
        </div>
      );
    });
  };

  useEffect(() => {
    calculateMaterials();
  }, [dimensions, selectedMaterial, furnitureType, shelvesCount]);

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6 text-center">Diseñador de Muebles de Cocina</h1>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-4">Parámetros del Mueble</h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Tipo de mueble</label>
              <select
                value={furnitureType.id}
                onChange={handleFurnitureTypeChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2 border"
              >
                {furnitureTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </div>

            {['width', 'height', 'depth'].map((dim) => (
              <div key={dim}>
                <label className="block text-sm font-medium text-gray-700">
                  {dim === 'width' ? 'Ancho' : dim === 'height' ? 'Alto' : 'Fondo'} (m)
                </label>
                <input
                  type="number"
                  name={dim}
                  value={dimensions[dim]}
                  onChange={handleDimensionChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2 border"
                  min="0.3"
                  step="0.1"
                />
              </div>
            ))}

            {furnitureType.shelves !== undefined && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Número de estantes</label>
                <input
                  type="number"
                  value={shelvesCount}
                  onChange={(e) => setShelvesCount(Math.max(0, parseInt(e.target.value) || 0))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2 border"
                  min="0"
                  max="10"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700">Material</label>
              <select
                value={selectedMaterial.id}
                onChange={(e) => setSelectedMaterial(materialTypes.find(m => m.id === parseInt(e.target.value)) || materialTypes[0])}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2 border"
              >
                {materialTypes.map(material => (
                  <option key={material.id} value={material.id}>{material.name}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="showEdges"
                checked={showEdges}
                onChange={(e) => setShowEdges(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
              />
              <label htmlFor="showEdges" className="ml-2 block text-sm text-gray-700">
                Mostrar bordes
              </label>
            </div>
          </div>

          <div className="mt-8 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-lg font-medium mb-3">Resultados</h3>
            <div className="space-y-4">
              <div>
                <h4 className="font-semibold mb-2">Paneles necesarios:</h4>
                <div className="max-h-40 overflow-y-auto">
                  {renderMaterialList()}
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">Materiales:</h4>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span className="text-sm">Área total utilizada:</span>
                    <span className="text-sm font-semibold">{formatDimension(results.materialUsed)} m²</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm">Paneles estándar equivalentes:</span>
                    <span className="text-sm font-semibold">{formatDimension(results.materialUsed / (1.2 * 2.4))}</span>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">Costos:</h4>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span className="text-sm">Materiales:</span>
                    <span className="text-sm font-semibold">{formatCurrency(results.panelCost)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm">Tornillos ({results.screws} und):</span>
                    <span className="text-sm font-semibold">{formatCurrency(results.screwsCost)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-1">
                    <span className="text-sm">Total estimado:</span>
                    <span className="text-sm font-semibold">{formatCurrency(results.totalCost)}</span>
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">Pesos:</h4>
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span className="text-sm">Paneles:</span>
                    <span className="text-sm font-semibold">{formatDimension(results.panelsWeight)} kg</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm">Tornillos:</span>
                    <span className="text-sm font-semibold">{formatDimension(results.screwsWeight)} kg</span>
                  </div>
                  <div className="flex justify-between border-t pt-1">
                    <span className="text-sm">Peso total:</span>
                    <span className="text-sm font-semibold">{formatDimension(results.totalWeight)} kg</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-4">Visualización 3D</h2>
          <div className="h-[700px] border border-gray-200 rounded-md">
            <Canvas shadows>
              <FurnitureScene 
                panels={panels}
                selectedPanel={selectedPanel}
                onSelect={setSelectedPanel}
                onTransform={handleTransformPanel}
                materials={materialTypes}
                furnitureType={furnitureType}
                dimensions={dimensions}
              />
            </Canvas>
          </div>
          <div className="mt-2 text-sm text-gray-500">
            <p>Selecciona un panel en la vista 3D para moverlo. Haz clic en espacio vacío para deseleccionar.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FurnitureDesigner;